import { useCallback, useEffect, useRef, useState } from 'react'

export type AutoSaveStatus = 'idle' | 'saving' | 'saved' | 'error'

interface UseAutoSaveOptions<T> {
  /** The key to use for localStorage */
  storageKey: string
  /** The data to save */
  data: T
  /** Whether auto-save is enabled (e.g., disabled when editing existing items) */
  enabled?: boolean
  /** Debounce delay in milliseconds (default: 3000ms = 3 seconds) */
  debounceMs?: number
  /** Optional validation function - return false to skip saving */
  shouldSave?: (data: T) => boolean
  /** Optional callback for server-side draft save */
  onServerSave?: (data: T) => Promise<void>
}

interface UseAutoSaveReturn<T> {
  /** Current auto-save status */
  status: AutoSaveStatus
  /** Last saved timestamp */
  lastSaved: Date | null
  /** Clear the draft from storage */
  clearDraft: () => void
  /** Load draft from storage (called automatically, but can be called manually) */
  loadDraft: () => T | null
  /** Force an immediate save */
  saveNow: () => void
  /** Whether there's a pending save */
  hasPendingChanges: boolean
}

/**
 * Hook for debounced auto-save to localStorage with optional server sync.
 * Saves form data after 3 seconds of inactivity to prevent data loss.
 *
 * Features:
 * - Debounced save (3 seconds by default)
 * - Status tracking (idle, saving, saved, error)
 * - Clears storage only after explicit clear call
 * - Handles browser close with beforeunload
 */
export function useAutoSave<T>({
  storageKey,
  data,
  enabled = true,
  debounceMs = 3000,
  shouldSave,
  onServerSave,
}: UseAutoSaveOptions<T>): UseAutoSaveReturn<T> {
  const [status, setStatus] = useState<AutoSaveStatus>('idle')
  const [lastSaved, setLastSaved] = useState<Date | null>(null)
  const [hasPendingChanges, setHasPendingChanges] = useState(false)

  // Refs to track the latest values without causing re-renders
  const dataRef = useRef(data)
  const timerRef = useRef<NodeJS.Timeout | null>(null)
  const isMountedRef = useRef(true)

  // Update dataRef when data changes
  useEffect(() => {
    dataRef.current = data
  }, [data])

  // Cleanup on unmount
  useEffect(() => {
    isMountedRef.current = true
    return () => {
      isMountedRef.current = false
      if (timerRef.current) {
        clearTimeout(timerRef.current)
      }
    }
  }, [])

  const saveToStorage = useCallback(
    async (dataToSave: T) => {
      if (!isMountedRef.current) return

      try {
        setStatus('saving')

        // Check if we should save
        if (shouldSave && !shouldSave(dataToSave)) {
          setStatus('idle')
          return
        }

        // Save to localStorage
        localStorage.setItem(storageKey, JSON.stringify(dataToSave))

        // Optional server save
        if (onServerSave) {
          try {
            await onServerSave(dataToSave)
          } catch (serverError) {
            console.warn('Server save failed, data saved locally:', serverError)
            // Don't fail - local save succeeded
          }
        }

        if (isMountedRef.current) {
          setStatus('saved')
          setLastSaved(new Date())
          setHasPendingChanges(false)

          // Reset to idle after 2 seconds
          setTimeout(() => {
            if (isMountedRef.current) {
              setStatus('idle')
            }
          }, 2000)
        }
      } catch (error) {
        console.error('Auto-save failed:', error)
        if (isMountedRef.current) {
          setStatus('error')
        }
      }
    },
    [storageKey, shouldSave, onServerSave],
  )

  // Debounced save effect
  useEffect(() => {
    if (!enabled) return

    // Mark as having pending changes
    setHasPendingChanges(true)

    // Clear existing timer
    if (timerRef.current) {
      clearTimeout(timerRef.current)
    }

    // Set new timer for debounced save
    timerRef.current = setTimeout(() => {
      saveToStorage(data)
    }, debounceMs)

    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current)
      }
    }
  }, [data, enabled, debounceMs, saveToStorage])

  // Handle browser close - save immediately if there are pending changes
  useEffect(() => {
    if (!enabled) return

    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (hasPendingChanges) {
        // Save synchronously before unload
        try {
          localStorage.setItem(storageKey, JSON.stringify(dataRef.current))
        } catch (error) {
          console.error('Failed to save on unload:', error)
        }
        // Show browser's default "unsaved changes" dialog
        e.preventDefault()
        e.returnValue = ''
      }
    }

    window.addEventListener('beforeunload', handleBeforeUnload)
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload)
    }
  }, [enabled, hasPendingChanges, storageKey])

  const clearDraft = useCallback(() => {
    try {
      localStorage.removeItem(storageKey)
      setStatus('idle')
      setLastSaved(null)
      setHasPendingChanges(false)
    } catch (error) {
      console.error('Failed to clear draft:', error)
    }
  }, [storageKey])

  const loadDraft = useCallback((): T | null => {
    try {
      const saved = localStorage.getItem(storageKey)
      if (saved) {
        return JSON.parse(saved) as T
      }
    } catch (error) {
      console.error('Failed to load draft:', error)
    }
    return null
  }, [storageKey])

  const saveNow = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current)
    }
    saveToStorage(dataRef.current)
  }, [saveToStorage])

  return {
    status,
    lastSaved,
    clearDraft,
    loadDraft,
    saveNow,
    hasPendingChanges,
  }
}

/**
 * Format the last saved time for display.
 * Returns a human-readable string like "Saved just now" or "Saved 2 minutes ago"
 */
export function formatLastSaved(lastSaved: Date | null): string {
  if (!lastSaved) return ''

  const now = new Date()
  const diffMs = now.getTime() - lastSaved.getTime()
  const diffSeconds = Math.floor(diffMs / 1000)
  const diffMinutes = Math.floor(diffSeconds / 60)

  if (diffSeconds < 10) {
    return 'Saved just now'
  } else if (diffSeconds < 60) {
    return `Saved ${diffSeconds} seconds ago`
  } else if (diffMinutes === 1) {
    return 'Saved 1 minute ago'
  } else if (diffMinutes < 60) {
    return `Saved ${diffMinutes} minutes ago`
  } else {
    return `Saved at ${lastSaved.toLocaleTimeString()}`
  }
}
